import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React, {ReactNode, useCallback, useEffect, useRef, useState} from 'react';

import * as analytics from 'analytics/Analytics';
import {ActionOrigin, WORKFLOWS_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {FLOWCHART_SIDEPANEL_Z_INDEX} from 'ui-library/reactflow/sidepanels/FlowchartSidebar';

export enum SidepanelStateChangeAction {
  OPEN = 'open',
  CLOSE = 'close',
  EXPAND = 'expand',
  COLLAPSE = 'collapse',
  SWITCH_TAB = 'switch_tab',
}

export enum PanelSide {
  LEFT = 'left',
  RIGHT = 'right',
}

export enum SidepanelSize {
  EXTRA_SMALL = 'extra_small',
  SMALL = 'small',
  MEDIUM = 'medium',
  FULL = 'full',
}

/**
 * The width of the sidepanel in pixels.
 *
 * For any size other than FULL, use numbers so that the value can be used
 * in calculations.
 */
export const sizeToWidth: Record<SidepanelSize, string | number> = {
  [SidepanelSize.EXTRA_SMALL]: 240,
  [SidepanelSize.SMALL]: 320,
  [SidepanelSize.MEDIUM]: 640,
  [SidepanelSize.FULL]: '100%',
};

export interface FlowchartContextPanelProps {
  panelSide: PanelSide;
  isOpen: boolean;
  size?: SidepanelSize;
  onClose: () => void;
  sidepanelName: string;
  /** Content to render inside the sidepanel */
  children: ReactNode;
  showExpandCollapseButtons?: boolean;
  sidepanelKey?: string;
  /** Flowchart type for analytics tracking. When provided, analytics are tracked automatically. */
  flowchartType?: string;
}

function getExpandCollapseButton(
  inputSize: SidepanelSize,
  currentPanelSize: SidepanelSize,
  handleExpand: () => void,
  handleCollapse: () => void
): JSX.Element | null {
  if (inputSize === SidepanelSize.FULL) {
    // Assume we can't collapse a full-width sidepanel for readability purposes
    // And we can't expand a full-width sidepanel further, so no buttons needed.
    return null;
  }

  if (currentPanelSize === SidepanelSize.FULL) {
    return (
      <button
        onClick={handleCollapse}
        aria-label="Collapse panel"
        {...getTestProps({
          component: 'FlowchartContextPanel',
          element: 'collapseButton',
        })}
        className={css(styles.expandButton)}
        type="button"
      >
        <BenchlingIcon icon="collapse-right-full" />
      </button>
    );
  }

  return (
    <button
      onClick={handleExpand}
      aria-label="Expand panel"
      {...getTestProps({
        component: 'FlowchartContextPanel',
        element: 'expandButton',
      })}
      className={css(styles.expandButton)}
      type="button"
    >
      <BenchlingIcon icon="expand-right-full" />
    </button>
  );
}

function HeaderButtons(props: {
  panelSide: PanelSide;
  isOpen: boolean;
  inputSize: SidepanelSize;
  currentPanelSize: SidepanelSize;
  onClose: () => void;
  handleExpand: () => void;
  handleCollapse: () => void;
  showExpandCollapseButtons?: boolean;
}): JSX.Element | null {
  if (props.panelSide === PanelSide.LEFT) {
    return (
      <div className={css(styles.headerButtons)}>
        <button
          onClick={props.onClose}
          aria-label="Close panel"
          {...getTestProps({
            component: 'FlowchartContextPanel',
            element: 'closeButton',
          })}
          className={css(styles.closeButton)}
          type="button"
        >
          <BenchlingIcon icon="chevron-left" />
        </button>
      </div>
    );
  }

  return (
    <div className={css(styles.headerButtons)}>
      {props.showExpandCollapseButtons &&
        getExpandCollapseButton(
          props.inputSize,
          props.currentPanelSize,
          props.handleExpand,
          props.handleCollapse
        )}
      <button
        onClick={props.onClose}
        aria-label="Close panel"
        {...getTestProps({
          component: 'FlowchartContextPanel',
          element: 'closeButton',
        })}
        className={css(styles.closeButton)}
        type="button"
      >
        <BenchlingIcon icon="close" />
      </button>
    </div>
  );
}
export function getStyleForSidepanelSize(size: SidepanelSize): {} {
  switch (size) {
    case SidepanelSize.FULL:
      return styles.sizeFull;
    case SidepanelSize.EXTRA_SMALL:
      return styles.sizeExtraSmall;
    case SidepanelSize.SMALL:
      return styles.sizeSmall;
    case SidepanelSize.MEDIUM:
    default:
      return styles.sizeMedium;
  }
}

/**
 * A sidepanel to use with ReactFlow flowcharts, supports left and right side panels
 * at different widths (including full width).
 *
 * Can use with @link{FlowchartSidebar}.
 *
 * @example
 * <FlowchartContextPanel
 *   panelSide={PanelSide.RIGHT}
 *   isOpen={isOpen}
 *   size={SidepanelSize.FULL}
 *   onClose={() => setIsOpen(false)}
 * >
 *   Your content here
 * </FlowchartContextPanel>
 *
 * @example
 * <FlowchartContextPanel
 *   panelSide={PanelSide.LEFT}
 *   isOpen={isOpen}
 *   size={SidepanelSize.MEDIUM}
 *   onClose={() => setIsOpen(false)}
 * >
 *   Your content here
 * </FlowchartContextPanel>
 */
export function FlowchartContextPanel({
  panelSide,
  isOpen,
  size = SidepanelSize.MEDIUM,
  onClose,
  children,
  sidepanelName,
  showExpandCollapseButtons = true,
  sidepanelKey,
  flowchartType,
}: FlowchartContextPanelProps): JSX.Element | null {
  const panelRef = useRef<HTMLDivElement>(null);
  const prevIsOpenRef = useRef(isOpen);
  const [panelSize, setPanelSize] = useState<SidepanelSize>(size);

  const trackStateChange = useCallback(
    (action: SidepanelStateChangeAction) => {
      if (flowchartType) {
        analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.SIDEPANEL_VISIBILITY_STATE_CHANGED, {
          actionOrigin: ActionOrigin.WORKFLOWS_FLOWCHART_FIRST_CONFIGURATION_CANVAS,
          sidepanelAction: action,
          sidepanelName,
          panelSide,
          flowchartType,
        });
      }
    },
    [flowchartType, sidepanelName, panelSide]
  );

  useEffect(() => {
    setPanelSize(size);
  }, [size, setPanelSize]);

  // Track OPEN state changes
  useEffect(() => {
    if (isOpen && !prevIsOpenRef.current) {
      trackStateChange(SidepanelStateChangeAction.OPEN);
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen, trackStateChange]);

  const handleExpand = useCallback(() => {
    setPanelSize(SidepanelSize.FULL);
    trackStateChange(SidepanelStateChangeAction.EXPAND);
  }, [setPanelSize, trackStateChange]);

  const handleCollapse = useCallback(() => {
    setPanelSize(size);
    trackStateChange(SidepanelStateChangeAction.COLLAPSE);
  }, [setPanelSize, size, trackStateChange]);

  const handleClose = useCallback(() => {
    trackStateChange(SidepanelStateChangeAction.CLOSE);
    onClose();
  }, [onClose, trackStateChange]);

  // Close sidepanel when Escape key is pressed.
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') {
        return;
      }

      if (event.defaultPrevented) {
        return;
      }

      // Check if focus is within the sidepanel, or if no specific element has focus
      const target = event.target as HTMLElement | null;
      const activeElement = document.activeElement;
      const focusWithinSidepanel = target != null && panelRef.current?.contains(target);
      const noSpecificFocus =
        activeElement == null ||
        activeElement === document.body ||
        activeElement === document.documentElement;

      if (focusWithinSidepanel || noSpecificFocus) {
        event.preventDefault();
        event.stopPropagation();
        handleClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, handleClose]);

  const sizeStyle = getStyleForSidepanelSize(panelSize);

  if (!isOpen) {
    return null;
  }

  return (
    <div
      ref={panelRef}
      className={css(
        styles.sidepanel,
        sizeStyle,
        panelSide === PanelSide.RIGHT && styles.rightSidepanel,
        panelSide === PanelSide.LEFT && styles.leftSidepanel
      )}
      {...getTestProps({
        component: 'FlowchartContextPanel',
        element: 'root',
        key: sidepanelKey,
      })}
    >
      <div
        className={css(styles.header)}
        {...getTestProps({
          component: 'FlowchartContextPanel',
          element: 'header',
        })}
      >
        <div
          className={css(styles.headerLeft)}
          {...getTestProps({
            component: 'FlowchartContextPanel',
            element: 'headerLeft',
          })}
        >
          <h2
            className={css(styles.sidepanelName)}
            {...getTestProps({
              component: 'FlowchartContextPanel',
              element: 'sidepanelName',
            })}
          >
            {sidepanelName}
          </h2>
        </div>
        <HeaderButtons
          panelSide={panelSide}
          isOpen={isOpen}
          onClose={handleClose}
          inputSize={size}
          currentPanelSize={panelSize}
          handleExpand={handleExpand}
          handleCollapse={handleCollapse}
          showExpandCollapseButtons={showExpandCollapseButtons}
        />
      </div>
      <div
        className={css(styles.content)}
        {...getTestProps({
          component: 'FlowchartContextPanel',
          element: 'content',
        })}
      >
        {children}
      </div>
    </div>
  );
}

export default FlowchartContextPanel;

// Re-export ContextPanelContent for convenience
export {ContextPanelContent} from './ContextPanelContent';

// Let the context panel control spacing instead of the tab container
export const tabPanelStyles = StyleSheet.create({
  noSpacing: {
    margin: 0,
    padding: 0,
  },
});

const styles = StyleSheet.create({
  sidepanel: {
    height: '100%',
    backgroundColor: Color.WHITE,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    position: 'relative',
    top: 0,
    zIndex: FLOWCHART_SIDEPANEL_Z_INDEX,
  },
  rightSidepanel: {
    borderLeft: `1px solid ${Color.GRAY_200}`,
    right: 0,
    position: 'absolute',
  },
  leftSidepanel: {
    borderRight: `1px solid ${Color.GRAY_200}`,
    left: 0,
    position: 'relative',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 12px',
    borderBottom: `1px solid ${Color.GRAY_200}`,
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  sidepanelName: {
    margin: 0,
    fontSize: '13px',
    fontWeight: 600,
  },
  headerButtons: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  expandButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '4px',
    ':hover': {
      backgroundColor: Color.GRAY_100,
    },
  },
  closeButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '4px',
    borderRadius: '4px',
    ':hover': {
      backgroundColor: Color.GRAY_100,
    },
  },
  content: {
    flex: 1,
    overflow: 'auto',
  },
  sizeFull: {
    width: sizeToWidth[SidepanelSize.FULL],
  },
  sizeExtraSmall: {
    width: sizeToWidth[SidepanelSize.EXTRA_SMALL],
  },
  sizeSmall: {
    width: sizeToWidth[SidepanelSize.SMALL],
  },
  sizeMedium: {
    width: sizeToWidth[SidepanelSize.MEDIUM],
  },
});
